/**
 * Award-data SSOT — AN-ACC (Australian National Aged Care Classification) funding
 *
 * Residential aged care funding model published by Department of Health,
 * Disability and Ageing via the AN-ACC Funding Guide (current v1.21 /
 * 1 April 2026).
 *
 * NOTE: This is regulator/program data, NOT a Fair Work modern award. It
 * sits in `award-data/` alongside the FWC awards for SSOT discipline.
 *
 * Funding model: AN-ACC Daily Basic Subsidy = AN_ACC_BASE_PRICE_DAILY × NWAU weight.
 *
 * The platform should DISPLAY the expected funding for each resident based on
 * their assigned classification, but the actual claim is submitted via the
 * Services Australia Aged Care Provider Portal (monthly).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Source: doh-schedule-subsidies-supplements-residential-2026-10-01.pdf
 *         (Schedule of subsidies and supplements - Residential Care, rates
 *         effective 1 October 2026, version 1, published 29/09/26).
 *         NWAU weights and structure: doh-an-acc-funding-guide-2026-03.pdf.
 * Verified: 2026-09-30.
 * Artifact: docs/operations/clause-verifications/2026-09-30-regulatory-indexation-20-sep-and-1-oct-2026.md
 *
 * These constants hold the CURRENT schedule only. Date-effective amounts live
 * in res_anacc_rates and rac_respite_fixed_component_rates.
 *
 * Indexation: typically reviewed annually by IHACPA. The Aged Care Outbreak
 * Management Support Supplement ceased 30 September 2025; current base price
 * includes outbreak management costs.
 *
 * IMPORTANT (1 April 2026 change): Standard MM1 NWAU was reduced from 0.50 to
 * 0.387. The remaining 0.113 NWAU has been redirected to the separate "care
 * minutes supplement". Total funding for MM1 BCT remains 0.50 NWAU when
 * including the supplement.
 */

// ═══════════════════════════════════════════════════════════════════════
// National Efficient Price
// ═══════════════════════════════════════════════════════════════════════

/**
 * AN-ACC National Efficient Price (per NWAU) — effective 1 October 2026
 * ($295.64 from 1 October 2025 to 30 September 2026).
 *
 * Source: doh-schedule-subsidies-supplements-residential-2026-10-01.pdf, page 1.
 */
export const AN_ACC_BASE_PRICE_DAILY = 303.19;

/**
 * Every national efficient price the platform has held. A `base_care_tariff`
 * equal to any of them is the price stored in the wrong column, whichever
 * period the row belongs to.
 */
export const AN_ACC_KNOWN_BASE_PRICES: readonly number[] = [295.64, AN_ACC_BASE_PRICE_DAILY];

// ═══════════════════════════════════════════════════════════════════════
// Permanent classification subsidies (Class 1-13)
// ═══════════════════════════════════════════════════════════════════════

/**
 * AN-ACC permanent resident classification subsidies — daily rates per class.
 *
 * Source: doh-schedule-subsidies-supplements-residential-2026-10-01.pdf, pages 1-2.
 * Effective 1 October 2026 (NWAU x $303.19, as published per person per day).
 *
 * Default classification rates (used while pending RAC funding assessment):
 *  - Permanent non-palliative residents: Class 8 ($181.91)
 *  - Palliative residents: Class 1 ($221.33)
 *  - Respite: Class 102 = Respite Class 2 ($174.03)
 *
 * 13 classes following a branching casemix model based on mobility, cognition,
 * function, pressure sore risk, and "compounding factors".
 */
export const AN_ACC_CLASS_DAILY_RATES: Record<number, number> = {
  1: 221.33, // Admit for palliative care (NWAU 0.73)
  2: 63.67, // Independent without compounding factors (NWAU 0.21)
  3: 121.28, // Independent with compounding factors (NWAU 0.40)
  4: 87.93, // Assisted mobility, high cognition, without compounding factors (NWAU 0.29)
  5: 130.37, // Assisted mobility, high cognition, with compounding factors (NWAU 0.43)
  6: 118.24, // Assisted mobility, medium cognition, without compounding factors (NWAU 0.39)
  7: 163.72, // Assisted mobility, medium cognition, with compounding factors (NWAU 0.54)
  8: 181.91, // Assisted mobility, low cognition (NWAU 0.60) — DEFAULT permanent non-palliative
  9: 160.69, // Not mobile, higher function, without compounding factors (NWAU 0.53)
  10: 178.88, // Not mobile, higher function, with compounding factors (NWAU 0.59)
  11: 206.17, // Not mobile, lower function, lower pressure sore risk (NWAU 0.68)
  12: 200.11, // Not mobile, lower function, higher pressure sore risk, no compounding (NWAU 0.66)
  13: 221.33, // Not mobile, lower function, higher pressure sore risk, with compounding (NWAU 0.73)
};

/**
 * AN-ACC permanent class NWAU weights (corresponding to AN_ACC_CLASS_DAILY_RATES).
 * Source: doh-an-acc-funding-guide-2026-03.pdf, Table 2, page 22.
 */
export const AN_ACC_CLASS_NWAU: Record<number, number> = {
  1: 0.73,
  2: 0.21,
  3: 0.4,
  4: 0.29,
  5: 0.43,
  6: 0.39,
  7: 0.54,
  8: 0.6,
  9: 0.53,
  10: 0.59,
  11: 0.68,
  12: 0.66,
  13: 0.73,
};

// ═══════════════════════════════════════════════════════════════════════
// Respite classification subsidies (Class 1-3)
// ═══════════════════════════════════════════════════════════════════════

/**
 * AN-ACC respite resident classification subsidies — daily rates per respite class.
 *
 * Source: doh-schedule-subsidies-supplements-residential-2026-10-01.pdf, page 2.
 * Effective 1 October 2026.
 *
 * 3 classes for respite residents, simpler model based on mobility only.
 */
export const AN_ACC_RESPITE_CLASS_DAILY_RATES: Record<number, number> = {
  1: 122.79, // Independent mobility (NWAU 0.405)
  2: 174.03, // Assisted mobility (NWAU 0.574) — DEFAULT for unclassified respite
  3: 216.48, // Not mobile (NWAU 0.714)
};

// ═══════════════════════════════════════════════════════════════════════
// Base Care Tariff (BCT)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Base Care Tariff (BCT) categories — FIXED funding component, paid per
 * occupied bed (MM1-5 + Specialised homeless) or per operational bed (MM6-7
 * + Specialised Aboriginal/Torres Strait Islander).
 *
 * Source: doh-schedule-subsidies-supplements-residential-2026-10-01.pdf, pages 2-3.
 * Effective 1 October 2026. The schedule publishes the four operational-place
 * categories to three decimals (206.169, 157.659, 236.488, 545.742) for working
 * out a home's daily total; they are held here to the cent.
 */
export const AN_ACC_BCT_CATEGORIES = {
  // Funded per occupied bed
  STANDARD_MM1: { nwau: 0.387, dailyPerBed: 117.33, basis: 'occupied' as const },
  STANDARD_MM2_3: { nwau: 0.53, dailyPerBed: 160.69, basis: 'occupied' as const },
  STANDARD_MM4_5: { nwau: 0.58, dailyPerBed: 175.85, basis: 'occupied' as const },
  SPECIALISED_HOMELESS: { nwau: 0.92, dailyPerBed: 278.93, basis: 'occupied' as const },
  // Funded per operational bed (MM 6-7)
  STANDARD_MM6_7_FIRST29: { nwau: 0.68, dailyPerBed: 206.17, basis: 'operational' as const },
  STANDARD_MM6_7_PLACES30PLUS: { nwau: 0.52, dailyPerBed: 157.66, basis: 'operational' as const },
  SPECIALISED_ATSI_MM6: { nwau: 0.78, dailyPerBed: 236.49, basis: 'operational' as const },
  SPECIALISED_ATSI_MM7: { nwau: 1.8, dailyPerBed: 545.74, basis: 'operational' as const },
} as const;

/**
 * Default AN-ACC classifications applied while a resident is awaiting their
 * RAC funding assessment outcome. Per Section 4.1.1 of the funding guide.
 */
export const AN_ACC_DEFAULT_CLASSES = {
  permanentNonPalliative: 8, // Class 8 = $181.91/day
  palliative: 1, // Class 1 = $221.33/day
  respite: 2, // Respite Class 2 = $174.03/day
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Daily subsidy decomposition — SINGLE SOURCE OF TRUTH for every UI surface
// ═══════════════════════════════════════════════════════════════════════
//
// Both the finance AN-ACC funding view and the residential AN-ACC classification
// register must express a resident's daily funding using this ONE helper so the
// two surfaces never disagree. It reconciles to the SSOT above:
//
//   daily subsidy = classification component  (variable — NWAU_class × NEP)
//                 + Base Care Tariff (BCT)     (fixed — region-aware per bed)
//                 + supplements                (per-resident add-ons)
//
// Regulated-money guards baked in:
//  1. `usedDefaultClass` — a missing/invalid classification never silently
//     yields a $0 class component; the Section 4.1.1 default (Class 8) is
//     substituted and flagged so the caller can label it "pending assessment".
//  2. `baseCareTariffSuspectedNep` — the res_anacc_rates row-semantics bug
//     stored the National Efficient Price (AN_ACC_BASE_PRICE_DAILY) in the
//     `base_care_tariff` column, which double-counts against the class
//     component (which is already NWAU × NEP). When the supplied BCT equals the
//     NEP the helper treats the BCT as $0 and raises this flag — conservative:
//     it under-states funding rather than over-stating it.

export interface AnAccDailySubsidyInput {
  /** AN-ACC permanent class (1-13). Null/0/out-of-range → Section 4.1.1 default applied. */
  classCode: number | string | null | undefined;
  /**
   * Base Care Tariff (BCT) for the facility — the fixed, region-aware per-bed
   * component (see AN_ACC_BCT_CATEGORIES). This is NOT the National Efficient
   * Price; supplying the NEP here is the double-count bug this helper guards.
   */
  baseCareTariff?: number | string | null;
  /** Classification component daily rates keyed by class code, e.g. AN_ACC_CLASS_DAILY_RATES. */
  classRates: Record<string, number | string> | null | undefined;
  /** Supplement daily rates keyed by supplement name (res_anacc_rates.supplement_rates). */
  supplementRates?: Record<string, number | string> | null;
  /** Per-resident supplements flags/amounts (res_anacc_classifications.supplements JSONB). */
  residentSupplements?: Record<string, unknown> | null;
  /**
   * Fold the BCT into the per-resident daily total. Default false — the BCT is a
   * facility-level per-bed component and should be reported as a SEPARATE
   * facility line, not baked into each resident's classification subsidy.
   */
  includeBaseCareTariff?: boolean;
}

export interface AnAccDailySubsidyBreakdown {
  classCode: number;
  classComponent: number;
  baseCareTariff: number;
  supplementTotal: number;
  dailyTotal: number;
  usedDefaultClass: boolean;
  baseCareTariffSuspectedNep: boolean;
}

function anAccMoney(value: number | string | null | undefined): number {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0);
  return Number.isFinite(n) ? (n as number) : 0;
}

/**
 * Decompose an AN-ACC daily subsidy from a rate source + a resident's
 * classification. See the block comment above for the reconciliation contract.
 */
export function decomposeAnAccDailySubsidy(
  input: AnAccDailySubsidyInput,
): AnAccDailySubsidyBreakdown {
  const rawClass = Number(input.classCode ?? NaN);
  const validClass = Number.isInteger(rawClass) && rawClass >= 1 && rawClass <= 13;
  const classCode = validClass ? rawClass : AN_ACC_DEFAULT_CLASSES.permanentNonPalliative;

  const classRates = input.classRates ?? {};
  const classComponent = anAccMoney(classRates[String(classCode)]);

  const rawBct = anAccMoney(input.baseCareTariff);
  // NEP double-count signature — see block comment guard (2).

  const baseCareTariffSuspectedNep = AN_ACC_KNOWN_BASE_PRICES.some(
    (price) => Math.abs(rawBct - price) < 0.005,
  );
  const baseCareTariff = baseCareTariffSuspectedNep ? 0 : rawBct;

  // Accumulate everything in integer cents to avoid float drift, then scale back
  // once at the end. Published AN-ACC rates are exact to the cent.
  const supplementRates = input.supplementRates ?? {};
  let supplementCents = 0;
  const residentSupplements = input.residentSupplements;
  if (residentSupplements && typeof residentSupplements === 'object') {
    for (const [key, val] of Object.entries(residentSupplements)) {
      if (val === true) {
        supplementCents += Math.round(anAccMoney(supplementRates[key]) * 100);
      } else if (typeof val === 'number' && Number.isFinite(val)) {
        supplementCents += Math.round(val * 100);
      }
    }
  }

  const bctForTotal = input.includeBaseCareTariff === true ? baseCareTariff : 0;

  const classCents = Math.round(classComponent * 100);
  // eslint-disable-next-line statura/no-floating-point-money -- scale dollar BCT to integer cents
  const bctCents = Math.round(bctForTotal * 100);
  const totalCents = classCents + bctCents + supplementCents;

  const supplementTotal = supplementCents / 100;

  const dailyTotal = totalCents / 100;

  return {
    classCode,
    classComponent,
    baseCareTariff,
    supplementTotal,
    dailyTotal,
    usedDefaultClass: !validClass,
    baseCareTariffSuspectedNep,
  };
}
